import { Component, EventEmitter, Output, Input, inject, OnInit, OnChanges, SimpleChanges, ViewChild, ElementRef, HostListener, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';
import { InstanceService } from '../../core/services/instance/instance.service';
import { Instance } from '../../models/instance';
import { BatchService } from '../../core/services/batch/batch.service';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DropdownAdditionalInfoIconComponent } from '../../shared/components/dropdown-additional-info-icon/dropdown-additional-info-icon.component';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { OverlayModule } from '@angular/cdk/overlay';
import { SortIconComponent } from '../../shared/components/sort-icon/sort-icon.component';
import { NgSelectComponent } from '@ng-select/ng-select';
import { CustomerService } from '../../core/services/customer.service';
import { ModalService } from '../../core/services/modal/modal.service';
import { HandIconComponent } from '../../shared/components/hand-icon/hand-icon.component';
import { CopyItemComponent } from '../../shared/components/copy-item/copy-item.component';
import { ListChevronsUpDownComponent } from '../../shared/components/list-chevrons-up-down/list-chevrons-up-down.component';
import { DeleteItemComponent } from '../../shared/components/delete-item/delete-item.component';


@Component({
  selector: 'app-edit-transactions-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LoadingSpinnerComponent,
    FormsModule,
    DropdownAdditionalInfoIconComponent,
    ScrollingModule,
    OverlayModule,
    SortIconComponent,
    NgSelectComponent,
    HandIconComponent,
    CopyItemComponent,
    ListChevronsUpDownComponent,
    DeleteItemComponent
  ],

  templateUrl: './edit-transactions-modal.component.html',
  styleUrls: ['./edit-transactions-modal.component.css'],
})
export class EditTransactionsModalComponent implements OnInit, OnChanges {

  translate = inject(TranslateService);
  instanceService = inject(InstanceService);
  customerService = inject(CustomerService);
  batchService = inject(BatchService);
  modalService = inject(ModalService);
  @Input() batchId: number;
  @Input() isOpen = false;
  @Input() results: any[] = [];
  @Input() manualFieldsOverrides: any[] = [];
  @Input() loading = false;
  @Input() editableBundleFields: any = null;
  @Input() customerId: any = null;
  @Output() close = new EventEmitter<void>();
  @Output() editTransactions = new EventEmitter<void>();
  instances: Instance[] = [];
  optionsMap: { [key: string]: any[] } = {};
  optionsIdentifierMap: { [key: string]: string } = {};
  modificationsMap: Map<string, any> = new Map();
  initialValuesMap: Map<string, any> = new Map();
  searchTerm = '';
  statusTerm = 'ALL';
  dropdownVisible = false;
  filterableFields: string[] = [];
  selectedFieldFilters: { [key: string]: any } = {};
  filterOptionsMap: { [key: string]: any[] } = {};
  additionalInfosForm = new FormGroup({});
  additionalColumns: any[] = [];
  selectedAdditionalInfos: any[] = [];
  sortColumn: string = 'date';
  sortDirection: 'asc' | 'desc' = 'desc';
  onOrderReference: 'asc' | 'desc' = 'desc';
  onOrderDate: 'asc' | 'desc' = 'desc';
  onOrderAmount: 'asc' | 'desc' = 'desc';
  additionalColumnsSort: { [key: string]: 'asc' | 'desc' } = {};
  selectedData: any = null;
  saving: boolean = false;
  numberTransactionsModified = signal<number>(0);
  onShowReference = signal(false);
  hasModifications: boolean = false;
  dependencyFields: string[] = [];
  @ViewChild('additionalInfoDropdown') additionalInfoDropdown: ElementRef;


  ngOnInit(): void {
    this.initEditableFields();
  }

  reset() {
    this.modificationsMap.clear();
    this.initialValuesMap.clear();
    this.searchTerm = '';
    this.dropdownVisible = false;
    this.selectedFieldFilters = {};
    this.additionalInfosForm.reset();
    this.additionalColumns = [];
    this.selectedAdditionalInfos = [];
    this.sortColumn = 'date';
    this.sortDirection = 'desc';
    this.onOrderReference = 'desc';
    this.onOrderDate = 'desc';
    this.onOrderAmount = 'desc';
    this.additionalColumnsSort = {};
    this.selectedData = null;
    this.numberTransactionsModified.set(0);
    this.hasModifications = false;
  }

  initEditableFields() {
    if (!this.editableBundleFields) return;
    this.filterableFields = [];
    Object.keys(this.editableBundleFields).forEach(key => {
      const config = this.editableBundleFields[key];
      if (config.dependency_field) {
        this.dependencyFields.push(config.dependency_field);
      }
      this.optionsIdentifierMap[key] = config.identifier_column;
      this.filterableFields.push(key);
      if (config.validation_rule?.includes('belongs_to_client')) {
        this.customerService.getConfigurationDataByCustomer(this.customerId, key.toUpperCase())
          .subscribe((data: any[]) => {
            data.unshift({
              name: "",
              id: null,
              category: {
                name: "",
                id: null
              }
            })
            this.optionsMap[key] = data;
          });
      }
    });
    this.generateFilterOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const isOpenChangedToTrue = changes['isOpen']?.currentValue === true;

    const shouldProcessData =
      (changes['results'] && this.results) ||
      (changes['manualFieldsOverrides'] && this.manualFieldsOverrides);

    if (isOpenChangedToTrue) {
      this.reset();
    }

    if ((isOpenChangedToTrue && this.results) || shouldProcessData) {
      this.processData();
    }

    if (changes['editableBundleFields'] && this.editableBundleFields) {
      this.initEditableFields();
    }
  }

  private processData(): void {
    this.mergeData(this.results, this.manualFieldsOverrides);
    this.extractAdditionalColumns();
    this.captureInitialValues();
    this.generateFilterOptions();
  }

  mergeData(results: any[], overrides: any[]) {
    if (!results || !overrides || overrides.length === 0) {
      if (this.numberTransactionsModified) {
        this.numberTransactionsModified.set(0);
      }
      this.hasModifications = false;
      return;
    }

    results.forEach(tx => {
      Object.keys(this.optionsIdentifierMap).forEach(column => {
        const idCol = this.optionsIdentifierMap[column];
        const txIdentifier = tx.additionalInfos?.[idCol];

        if (txIdentifier) {
          const matchingOverride = overrides.find(o => o.identifier === txIdentifier);
          if (matchingOverride && matchingOverride.additionalInfos) {
            Object.keys(matchingOverride.additionalInfos).forEach(infoKey => {
              tx.additionalInfos[infoKey] = matchingOverride.additionalInfos[infoKey];
              tx.isModified = true;
            });
          }
        }
      });
    });
    this.hasModifications = this.numberTransactionsModified() > 0;
  }

  captureInitialValues() {
    this.results.forEach(tx => {
      Object.keys(this.optionsIdentifierMap).forEach(col => {
        const idCol = this.optionsIdentifierMap[col];
        const identifier = tx.additionalInfos[idCol];
        if (identifier) {
          if (!this.initialValuesMap.has(identifier)) {
            this.initialValuesMap.set(identifier, { ...tx.additionalInfos });
          }
        }
      });
    });
  }

  generateFilterOptions() {
    this.filterOptionsMap = {};
    if (!this.results || !this.filterableFields.length) return;

    this.filterableFields.forEach(field => {
      const values = new Set<string>();
      this.results.forEach(tx => {
        const val = tx.additionalInfos?.[field];
        if (val) {
          values.add(val);
        }
      });
      this.filterOptionsMap[field] = Array.from(values).sort();
    });
  }

  extractAdditionalColumns() {
    const keys = new Set<string>();
    this.results.forEach(tx => {
      if (tx.additionalInfos) {
        Object.keys(tx.additionalInfos).forEach(key => keys.add(key));
      }
    });
    const newColumns: any[] = [];
    keys.forEach(key => {
      const existing = this.additionalColumns.find(col => col.label === key);
      newColumns.push({
        label: key,
        show: existing ? existing.show : false
      });
    });
    this.additionalColumns = newColumns;
  }

  get filteredResults(): any[] {
    let results = [...this.results];
    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      results = results.filter(tx =>
        (tx.reference && tx.reference.toLowerCase().includes(term)) ||
        (tx.date && tx.date.toLowerCase().includes(term)) ||
        (tx.amount && String(tx.amount).toLowerCase().includes(term)) ||
        this.selectedAdditionalInfos.some(col => tx.additionalInfos && tx.additionalInfos[col.label] && tx.additionalInfos[col.label].toLowerCase().includes(term))
      );
    }
    if (this.statusTerm && this.statusTerm !== 'ALL') {
      if (this.statusTerm === 'NO_CLASSIFIED_ONLY') {
        results = results.filter(tx => !tx.additionalInfos?.['instance']);
      } else if (this.statusTerm === 'CLASSIFIED') {
        results = results.filter(tx => tx.additionalInfos?.['instance']);
      }
    }

    if (this.filterableFields && this.filterableFields.length > 0) {
      this.filterableFields.forEach(field => {
        const filterVal = this.selectedFieldFilters[field];
        if (filterVal && filterVal.length > 0) {
          results = results.filter(tx => filterVal.includes(tx.additionalInfos[field]));
        }
      });
    }

    if (this.sortColumn) {
      results.sort((a, b) => {
        let valA, valB;

        if (this.sortColumn === 'reference' || this.sortColumn === 'date' || this.sortColumn === 'amount') {
          valA = a[this.sortColumn];
          valB = b[this.sortColumn];
        } else {
          valA = a.additionalInfos ? a.additionalInfos[this.sortColumn] : '';
          valB = b.additionalInfos ? b.additionalInfos[this.sortColumn] : '';
        }

        if (this.sortColumn === 'amount') {
          valA = Number(valA) || 0;
          valB = Number(valB) || 0;
        }

        if (this.sortColumn === 'date') {
          valA = valA ? new Date(valA).getTime() : 0;
          valB = valB ? new Date(valB).getTime() : 0;
        }

        if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return results;
  }

  onFilterChange(changedField: string) {
    const config = this.editableBundleFields[changedField];
    if (config && config.dependency_field) {
      const depField = config.dependency_field;
      const selectedValues = this.selectedFieldFilters[changedField];
      const newOptions = new Set<string>();
      if (selectedValues && selectedValues.length > 0) {
        this.filteredResults.forEach(tx => {
          const val = tx.additionalInfos?.[changedField];
          if (val && selectedValues.includes(val)) {
            const depVal = tx.additionalInfos?.[depField];
            if (depVal) {
              newOptions.add(depVal);
            }
          }
        });
      } else {
        this.filteredResults.forEach(tx => {
          const depVal = tx.additionalInfos?.[depField];
          if (depVal) {
            newOptions.add(depVal);
          }
        });
      }
      this.filterOptionsMap[depField] = Array.from(newOptions).sort();

      if (this.selectedFieldFilters[depField]) {
        this.selectedFieldFilters[depField] = this.selectedFieldFilters[depField].filter(
          (v: string) => newOptions.has(v)
        );
      }
    }
  }

  onSearch(event: Event) {
    this.searchTerm = (event.target as HTMLInputElement).value;
  }

  onStatusChange(event: Event) {
    this.statusTerm = (event.target as HTMLSelectElement).value;
  }

  closeModal() {
    this.restoreInitialValues();
    this.reset();
    this.close.emit();
  }

  restoreInitialValues() {
    if (this.modificationsMap.size === 0) return;

    this.results.forEach(tx => {
      let identifier: string | null = null;
      for (const col of Object.keys(this.optionsIdentifierMap)) {
        const idCol = this.optionsIdentifierMap[col];
        if (tx.additionalInfos && tx.additionalInfos[idCol]) {
          identifier = tx.additionalInfos[idCol];
          break;
        }
      }

      if (identifier && this.initialValuesMap.has(identifier) && this.modificationsMap.has(identifier)) {
        tx.additionalInfos = { ...this.initialValuesMap.get(identifier) };
      }
    });
  }

  editTransactionsModal() {
    const modifications = Array.from(this.modificationsMap.values());
    if (modifications.length === 0) {
      this.modalService.notifier(this.translate.instant("EDIT_TRANSACTION.MESSAGE.ERROR"), "", "error");
      this.closeModal();
      return;
    }

    this.saving = true;

    this.batchService.updateBundleTransactions(this.batchId, modifications).subscribe({
      next: (res: any) => {
        this.manualFieldsOverrides.push(...res);
        this.saving = false;
        this.modificationsMap.clear();
        this.processData();
        this.editTransactions.emit();
        this.modalService.notifier(this.translate.instant("EDIT_TRANSACTION.MESSAGE.SUCCESS"), "", "success");
      },
      error: (err: any) => {
        this.modalService.notifier(this.translate.instant("EDIT_TRANSACTION.MESSAGE.ERROR"), "", "error");
        this.saving = false;
      }
    });
  }

  toggleDropdownAdditionalInfos(event: Event) {
    event.stopPropagation();
    this.dropdownVisible = !this.dropdownVisible;
  }

  isRowModified(tx: any, column: string): boolean {
    const idCol = this.optionsIdentifierMap[column];
    if (!idCol || !tx.additionalInfos) return false;
    const identifier = tx.additionalInfos[idCol];
    return identifier ? this.modificationsMap.has(identifier) : false;
  }

  onCheckAdditionalInfo(info: any, event: any) {
    info.show = event.target.checked;
    if (
      info.show === true &&
      !this.selectedAdditionalInfos.some((elt) => elt.label === info.label)
    ) {
      this.selectedAdditionalInfos.unshift(info);
    }
    else if (
      info.show === false &&
      this.selectedAdditionalInfos.some((elt) => elt.label === info.label)
    )
      this.selectedAdditionalInfos = this.selectedAdditionalInfos.filter(
        (elt) => elt.label !== info.label
      );
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }

    if (column === 'reference') this.onOrderReference = this.sortDirection;
    if (column === 'date') this.onOrderDate = this.sortDirection;
    if (column === 'amount') this.onOrderAmount = this.sortDirection;
  }

  onOptionalFieldSort(info: any) {
    const column = info.label;
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.additionalColumnsSort[column] = this.sortDirection;
  }

  selectData(item: any, tx: any, column: string) {
    const idCol = this.optionsIdentifierMap[column];
    const identifier = tx.additionalInfos[idCol];
    if (!identifier) return;

    const initialVal = this.initialValuesMap.get(identifier)?.[column];
    if (item === initialVal) {
      const mod = this.modificationsMap.get(identifier);
      if (mod && mod.additionalInfos) {
        delete mod.additionalInfos[column];
        const keys = Object.keys(mod.additionalInfos);
        if (keys.length === 0) {
          this.modificationsMap.delete(identifier);
        }
      }
    } else {
      let mod = this.modificationsMap.get(identifier);
      if (!mod) {
        mod = { identifier: identifier, additionalInfos: {} };
        this.modificationsMap.set(identifier, mod);
        this.numberTransactionsModified.update(value => this.modificationsMap.size);
        this.hasModifications = this.numberTransactionsModified() > 0;
      }
      mod.additionalInfos[column] = item['name'];
      mod.additionalInfos['category'] = item['category']['name'];
      mod.additionalInfos['instance_id'] = item['id'];
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.additionalInfoDropdown && !this.additionalInfoDropdown.nativeElement.contains(event.target)) {
      this.dropdownVisible = false;
    }
  }

  getReferenceDisplayValue(reference: string) {
    return this.onShowReference() ? reference : reference.substring(0, 20) + '...';
  }


}
